<template>
  <ElImageViewer v-if="!mobilePreview" v-bind="$attrs" :url-list="urlList" :initial-index="activeIndex" @close="$emit('close')" @switch="handleSwitch">
    <template v-for="(_, name) in $slots" #[name]="scope"><slot :name="name" v-bind="scope || {}" /></template>
  </ElImageViewer>
</template>

<script>
import { ElImageViewer } from 'element-plus'
import { markRaw } from 'vue'
import { createImageViewer, destroyImageViewer } from '@/utils/ImageViewer'
import { isPhoneWorkspace } from '@/views/mobile/shared/displayState'
import { useDisplayState } from '@/views/mobile/shared/useDisplayState'

let viewerSequence = 0

export default {
  name: 'AppImageViewer',
  inheritAttrs: false,
  components: { ElImageViewer },
  setup() { return useDisplayState() },
  props: {
    urlList: { type: Array, required: true },
    initialIndex: { type: Number, default: 0 },
    download: { type: Function, default: null },
    error: Boolean,
    retry: { type: Function, default: null }
  },
  emits: ['close', 'closed', 'open', 'switch', 'loaded', 'error'],
  data() { return { viewer: null, viewerId: `mobile-image-preview-${++viewerSequence}`, disposed: false, activeIndex: this.initialIndex, viewerSources: '' } },
  computed: {
    mobilePreview() { return this.displayState.layoutMode === 'mobile' || isPhoneWorkspace(this.displayState) },
    dialogContentRef() { return this.viewer?.element || null }
  },
  watch: {
    urlList: { deep: true, handler() { this.syncViewer() } },
    initialIndex(value) { this.activeIndex = value; this.syncViewer() },
    error(value) { this.viewer?.setError(value) },
    mobilePreview() { this.syncViewer() }
  },
  mounted() {
    this.syncViewer()
    this.$emit('open')
  },
  beforeUnmount() {
    this.disposed = true
    destroyImageViewer(this.viewerId)
    this.viewer = null
    this.$emit('closed')
  },
  methods: {
    handleSwitch(index) {
      this.activeIndex = index
      this.viewerSources = JSON.stringify([this.urlList, index])
      this.$emit('switch', index)
    },
    syncViewer() {
      if (this.disposed) return
      if (!this.mobilePreview) {
        destroyImageViewer(this.viewerId)
        this.viewer = null
        this.viewerSources = ''
        return
      }
      const sources = JSON.stringify([this.urlList, this.activeIndex])
      if (this.viewer && this.viewerSources === sources) return
      if (!this.viewer) this.viewer = markRaw(createImageViewer({
        containerId: this.viewerId,
        labels: {
          close: this.$t('components.vueblocknote.close'),
          zoomOut: this.$t('components.vueblocknote.zoom_out'),
          zoomIn: this.$t('components.vueblocknote.zoom_in'),
          originalSize: this.$t('components.vueblocknote.original_size'),
          fitToScreen: this.$t('components.vueblocknote.fit_to_screen'),
          rotateLeft: this.$t('components.vueblocknote.rotate_left'),
          rotateRight: this.$t('components.vueblocknote.rotate_right'),
          previous: this.$t('views.image_generation.previous_case'),
          next: this.$t('views.image_generation.next_case'),
          download: this.$t('components.vueblocknote.download_image'),
          loading: this.$t('views.attachment.loading_preview'),
          failed: this.$t('views.attachment.preview_failed'),
          retry: this.$t('views.attachment.preview_retry')
        },
        // 横竖屏切换仅替换查看器，不能关闭父级的预览状态。
        onClose: () => { if (!this.disposed && this.mobilePreview) this.$emit('close') },
        onDownload: this.download,
        onSwitch: this.handleSwitch,
        onLoad: () => this.$emit('loaded'),
        onError: () => this.$emit('error'),
        onRetry: this.retry
      }))
      this.viewerSources = sources
      this.viewer.show(this.urlList, this.activeIndex)
      this.viewer.setError(this.error)
    }
  }
}
</script>
